import { useState, useEffect } from 'react';
import { api } from '../App';

// Cache global pour éviter les appels répétés
const configCache = new Map();
const cacheTimestamp = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook pour charger les ConfigOptions depuis l'API
 * @param {string|string[]} categories - Une catégorie ou un tableau de catégories
 * @returns {{ options: Object, loading: boolean, error: Error|null }}
 */
export function useConfig(categories) {
  // Stabilize categories string to avoid re-renders
  const categoriesKey = Array.isArray(categories) ? categories.sort().join(',') : categories;
  
  const [state, setState] = useState(() => {
    // Initialize from cache if available
    const initial = {};
    const cats = categoriesKey.split(',');
    let allCached = true;
    cats.forEach(cat => {
      const cached = configCache.get(cat);
      const timestamp = cacheTimestamp.get(cat);
      if (cached && timestamp && Date.now() - timestamp < CACHE_TTL) {
        initial[cat] = cached;
      } else {
        initial[cat] = [];
        allCached = false;
      }
    });
    return { options: initial, loading: !allCached, error: null };
  });
  
  useEffect(() => {
    const categoryList = categoriesKey.split(',');
    let cancelled = false;
    
    const fetchConfig = async () => {
      try {
        const result = {};
        const toFetch = [];

        // Check cache first
        for (const cat of categoryList) {
          const cached = configCache.get(cat);
          const timestamp = cacheTimestamp.get(cat);
          if (cached && timestamp && Date.now() - timestamp < CACHE_TTL) {
            result[cat] = cached;
          } else {
            toFetch.push(cat);
          }
        }

        // If everything is cached, use cache
        if (toFetch.length === 0) {
          if (!cancelled) {
            setState({ options: result, loading: false, error: null });
          }
          return;
        }
        
        // Fetch missing categories
        const response = await api.get(`/config/select?categories=${toFetch.join(',')}`);
        
        for (const cat of toFetch) {
          let data = response.data[cat] || [];
          
          // Deduplicate by code - keep only the first occurrence of each code
          // This handles cases where database has duplicate config entries
          const seenCodes = new Set();
          data = data.filter(item => {
            if (seenCodes.has(item.code)) {
              return false;
            }
            seenCodes.add(item.code);
            return true;
          });
          
          configCache.set(cat, data);
          cacheTimestamp.set(cat, Date.now());
          result[cat] = data;
        }
        
        // Fill in cached data for categories not fetched
        for (const cat of categoryList) {
          if (!result[cat]) {
            result[cat] = configCache.get(cat) || [];
          }
        }

        if (!cancelled) {
          setState({ options: result, loading: false, error: null });
        }
      } catch (err) {
        console.error('Error fetching config:', err);
        if (!cancelled) {
          setState(prev => ({ ...prev, loading: false, error: err }));
        }
      }
    };

    // Always fetch if not all cached
    const hasMissingData = categoriesKey.split(',').some(cat => {
      const cached = configCache.get(cat);
      const timestamp = cacheTimestamp.get(cat);
      return !cached || !timestamp || Date.now() - timestamp >= CACHE_TTL;
    });
    
    if (hasMissingData) {
      fetchConfig();
    } else {
      // All cached, just update state
      const result = {};
      categoriesKey.split(',').forEach(cat => {
        result[cat] = configCache.get(cat) || [];
      });
      setState({ options: result, loading: false, error: null });
    }
    
    return () => {
      cancelled = true;
    };
  }, [categoriesKey]);

  return state;
}

/**
 * Invalidate cache for specific categories or all
 */
export function invalidateConfigCache(categories = null) {
  if (categories) {
    const cats = Array.isArray(categories) ? categories : [categories];
    cats.forEach(cat => {
      configCache.delete(cat);
      cacheTimestamp.delete(cat);
    });
  } else {
    configCache.clear();
    cacheTimestamp.clear();
  }
}

// Default fallbacks for backwards compatibility
export const FALLBACK_STATUS_LABELS = {
  PENDING: 'En attente',
  CONFIRMED: 'Confirmée',
  CHECKED_IN: 'En cours',
  CHECKED_OUT: 'Terminée',
  CANCELLED: 'Annulée',
  NO_SHOW: 'No-show',
  BLOCKED: 'Bloquée',
  MAINTENANCE: 'Maintenance'
};

export const FALLBACK_EVENT_TYPES = {
  MEAL: 'Repas',
  TAXI: 'Taxi / Transfert',
  ACTIVITY: 'Activité',
  SPA: 'Spa / Bien-être',
  EXCURSION: 'Excursion',
  OTHER: 'Autre'
};

export const FALLBACK_EVENT_STATUS = {
  PENDING: 'En attente',
  CONFIRMED: 'Confirmé',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé'
};

export const FALLBACK_PAYMENT_METHODS = {
  CASH: 'Espèces',
  CARD: 'Carte bancaire',
  TRANSFER: 'Virement',
  CHECK: 'Chèque',
  PLATFORM: 'Plateforme',
  TOURIST_TAX: 'Taxe séjour',
  OTHER: 'Autre'
};

export const FALLBACK_PAYMENT_TYPES = {
  DEPOSIT: 'Acompte',
  BALANCE: 'Solde',
  FULL: 'Paiement total'
};

export const FALLBACK_CURRENCIES = {
  MAD: 'MAD',
  EUR: 'EUR',
  USD: 'USD',
  GBP: 'GBP'
};

export default useConfig;
